import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ImpersonationBanner from '../components/ImpersonationBanner';
import BrandLogo from '../components/brand/BrandLogo';
import ProductGuide from '../components/product-guide/ProductGuide';
import LocaleSwitch from '../components/LocaleSwitch';
import { useLocale } from '../i18n/LocaleContext';
import { approvals as approvalsApi, tasks as tasksApi } from '../lib/api';
import {
  Inbox,
  Users,
  Settings,
  LogOut,
  Shield,
  BarChart3,
  CheckSquare,
  FileText,
  CalendarDays,
  ClipboardCheck,
  ListTodo,
  Network,
  Menu,
  X,
} from 'lucide-react';

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingReplyApprovals, setPendingReplyApprovals] = useState(0);
  const [openTaskCount, setOpenTaskCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const userId = user?.id;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const postsTo = isAdmin && !user?.impersonating ? '/admin/posts' : '/app/posts';

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userId) return undefined;
    let mounted = true;
    const loadPendingReplyApprovals = async () => {
      try {
        const data = await approvalsApi.list('pending');
        if (mounted) setPendingReplyApprovals(data.total ?? (data.items || []).length);
      } catch {
        if (mounted) setPendingReplyApprovals(0);
      }
    };
    loadPendingReplyApprovals();
    const handleApprovalsChanged = () => {
      loadPendingReplyApprovals();
    };
    window.addEventListener('linirio:approvals-changed', handleApprovalsChanged);
    const id = setInterval(loadPendingReplyApprovals, 60000);
    return () => {
      mounted = false;
      window.removeEventListener('linirio:approvals-changed', handleApprovalsChanged);
      clearInterval(id);
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    let mounted = true;
    const loadOpenTasks = async () => {
      try {
        const data = await tasksApi.list({ status: 'active', limit: 1 });
        if (mounted) setOpenTaskCount(data.total ?? (data.items || []).length);
      } catch {
        if (mounted) setOpenTaskCount(0);
      }
    };
    loadOpenTasks();
    const handleTasksChanged = () => {
      loadOpenTasks();
    };
    window.addEventListener('linirio:tasks-changed', handleTasksChanged);
    const id = setInterval(loadOpenTasks, 60000);
    return () => {
      mounted = false;
      window.removeEventListener('linirio:tasks-changed', handleTasksChanged);
      clearInterval(id);
    };
  }, [userId]);

  const navItems = useMemo(() => {
    const items = [
      { to: '/app', icon: Inbox, label: t('Inbox'), end: true },
      { to: '/app/leads', icon: Users, label: t('Leads') },
      { to: '/app/engagement', icon: Network, label: t('Engagement') },
      ...((isAdmin && !user?.impersonating) ? [] : [{ to: '/app/tasks', icon: ListTodo, label: t('Tasks'), badge: openTaskCount }]),
      { to: '/app/approvals', icon: CheckSquare, label: t('Approvals'), badge: pendingReplyApprovals },
      { to: postsTo, icon: FileText, label: t('Posts') },
      { to: '/app/reports', icon: BarChart3, label: t('Reports') },
      { to: '/app/calendar', icon: CalendarDays, label: t('Calendar') },
      { to: '/app/audit', icon: ClipboardCheck, label: t('Audit') },
      { to: '/app/settings', icon: Settings, label: t('Settings') },
    ];

    if (isAdmin) {
      items.push({ to: '/admin', icon: Shield, label: t('Admin') });
    }

    return items;
  }, [isAdmin, openTaskCount, pendingReplyApprovals, postsTo, t, user?.impersonating]);

  const mobileNavItems = useMemo(() => {
    if (isAdmin && !user?.impersonating) {
      return [
        { to: '/app', icon: Inbox, label: 'Inbox', end: true },
        { to: '/app/approvals', icon: CheckSquare, label: 'Approvals', badge: pendingReplyApprovals },
        { to: '/admin', icon: Shield, label: 'Admin' },
        { to: '/app/calendar', icon: CalendarDays, label: 'Calendar' },
        { to: '/app/settings', icon: Settings, label: 'Settings' },
      ];
    }

    return [
      { to: '/app', icon: Inbox, label: 'Inbox', end: true },
      { to: '/app/tasks', icon: ListTodo, label: 'Tasks', badge: openTaskCount },
      { to: '/app/approvals', icon: CheckSquare, label: 'Approvals', badge: pendingReplyApprovals },
      { to: '/app/calendar', icon: CalendarDays, label: 'Calendar' },
      { to: '/app/settings', icon: Settings, label: 'Settings' },
    ];
  }, [isAdmin, openTaskCount, pendingReplyApprovals, user?.impersonating]);

  return (
    <div className="app-shell-root bg-[#F7F8F6] text-[#0B1D3A] flex flex-col">
      <ImpersonationBanner />

      <header className="lg:hidden sticky top-0 z-30 h-16 border-b border-[#D7DCE4] bg-[#F7F8F6]/95 backdrop-blur">
        <div className="relative h-full px-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DCE4] bg-white text-[#0B1D3A]"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => navigate('/app')}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <BrandLogo variant="icon" tone="light" size="md" loading="eager" />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <LocaleSwitch compact />
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DCE4] bg-white text-[#6B7280]"
              aria-label={t('Sign out')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 bg-[#0B1D3A]/24"
            aria-label="Close navigation overlay"
          />
          <aside className="relative h-full w-[18rem] max-w-[85vw] bg-[#F7F8F6] border-r border-[#D7DCE4] shadow-2xl flex flex-col">
            <div className="h-16 px-4 border-b border-[#D7DCE4] flex items-center justify-between">
              <BrandLogo variant="horizontal" tone="light" size="md" loading="eager" />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#D7DCE4] bg-white text-[#6B7280]"
                aria-label="Close navigation"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1">
              {navItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </nav>
            <div className="border-t border-[#D7DCE4] px-4 py-4">
              <div className="flex items-center gap-3 rounded-2xl border border-[#D7DCE4] bg-white px-3 py-3">
                <div className="w-10 h-10 rounded-full bg-[#E6FAF9] flex items-center justify-center text-sm font-semibold text-[#0B1D3A]">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0B1D3A] truncate">{user?.name}</p>
                  <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        <aside className="hidden lg:flex w-72 border-r border-[#D7DCE4] flex-col bg-[#F7F8F6]">
          <div className="h-16 flex items-center px-5 border-b border-[#D7DCE4]">
            <button type="button" onClick={() => navigate('/app')} className="flex items-center">
              <BrandLogo variant="horizontal" tone="light" size="md" loading="eager" />
            </button>
          </div>

          <nav className="flex-1 py-5 px-3 space-y-1 overflow-y-auto overflow-x-hidden">
            {navItems.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </nav>

          <div className="p-4 border-t border-[#D7DCE4]">
            <div className="rounded-2xl border border-[#D7DCE4] bg-white px-3 py-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E6FAF9] flex items-center justify-center text-sm font-semibold text-[#0B1D3A]">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0B1D3A] truncate">{user?.name}</p>
                  <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LocaleSwitch />
              <button
                onClick={handleLogout}
                className="min-w-0 flex-1 flex items-center gap-3 px-3 py-2.5 text-sm text-[#6B7280] hover:bg-white hover:text-[#0B1D3A] rounded-xl transition-all border border-transparent hover:border-[#D7DCE4]"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                {t('Sign out')}
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 min-h-0 overflow-hidden pb-20 lg:pb-0">
          <Outlet />
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#D7DCE4] bg-[#F7F8F6]/95 backdrop-blur">
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-medium ${
                  isActive
                    ? 'bg-[#E6FAF9] text-[#0B1D3A]'
                    : 'text-[#6B7280]'
                }`
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{item.label}</span>
              {item.badge ? (
                <span className="absolute top-1.5 right-3 min-w-4 h-4 px-1 rounded-full bg-[#0B1D3A] text-white text-[10px] font-semibold flex items-center justify-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      </nav>

      <ProductGuide />
    </div>
  );
}

function NavItem({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all ${
          isActive
            ? 'bg-[#E6FAF9] text-[#0B1D3A] shadow-[inset_3px_0_0_0_#12C8C6]'
            : 'text-[#6B7280] hover:bg-white hover:text-[#0B1D3A]'
        }`
      }
    >
      <item.icon className="w-5 h-5 shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#0B1D3A] text-white text-[11px] font-semibold flex items-center justify-center tabular-nums">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </NavLink>
  );
}
